/**
 * Link-preview (Open Graph / Twitter) helpers shared by every public route.
 *
 * Crawlers (WhatsApp, iMessage, Facebook, LinkedIn, Slack, Telegram) are much
 * pickier than browsers: they want an absolute https image URL, prefer JPEG
 * over WebP, and render the card faster when width/height are declared.
 */

import { isVideo } from '../utils/media.js';
import { resolveShareTitle } from './titleTemplate.js';

export const SHARE_IMAGE_WIDTH = 1200;
const SHARE_IMAGE_QUALITY = 80;

const VIDEO_PATH = /\.(mp4|webm|mov|m4v)(?:[?#]|$)/i;

/**
 * Site title for share cards. Appearance stores it on the theme; older sites
 * still have it in properties.
 * @param {object|null|undefined} content
 * @param {object|null|undefined} theme compiled live theme from AppContext
 */
export function shareSiteTitle(content, theme) {
    return String(
        theme?.brand?.siteTitle
        || content?.properties?.site_title
        || content?.properties?.meta_title
        || '',
    ).trim();
}

function largestSrcset(srcset) {
    if (!srcset || typeof srcset !== 'object') return null;
    const widths = Object.keys(srcset).map(Number).filter(Number.isFinite).sort((a, b) => b - a);
    return widths.length ? srcset[widths[0]] || srcset[String(widths[0])] : null;
}

/** Relative path of the still to share (the poster for videos). */
export function shareImageSource(file) {
    const url = file?.url;
    if (!url) return null;
    const original = typeof url.original === 'string' && !VIDEO_PATH.test(url.original) ? url.original : null;
    const poster = typeof url.poster === 'string' && !VIDEO_PATH.test(url.poster) ? url.poster : null;
    return (isVideo(file) ? poster || original : original || poster) || largestSrcset(url.srcset) || null;
}

/**
 * Absolute JPEG URL for a file, resized through the image optimiser.
 * @param {object} file
 * @param {string} baseUrl origin without trailing slash
 */
export function shareImageUrl(file, baseUrl) {
    const src = shareImageSource(file);
    if (!src) return null;
    if (/^https?:\/\//i.test(src)) return src;
    const base = String(baseUrl || '').replace(/\/$/, '');
    const query = new URLSearchParams({
        url: src.replace(/^\/+/, ''),
        w: String(SHARE_IMAGE_WIDTH),
        q: String(SHARE_IMAGE_QUALITY),
        f: 'jpeg',
    });
    return `${base}/_next/image?${query.toString()}`;
}

/**
 * og:image + twitter:image tags (with size hints when known).
 * @returns {Array<{ property?: string, name?: string, content: string }>}
 */
export function shareImageMeta(file, baseUrl) {
    const image = shareImageUrl(file, baseUrl);
    if (!image) return [];
    const tags = [
        { property: 'og:image', content: image },
        { property: 'og:image:type', content: 'image/jpeg' },
        { name: 'twitter:image', content: image },
    ];
    const width = Number(file?.dimensions?.width);
    const height = Number(file?.dimensions?.height);
    if (width > 0 && height > 0) {
        const outWidth = Math.min(SHARE_IMAGE_WIDTH, width);
        tags.push(
            { property: 'og:image:width', content: String(outWidth) },
            { property: 'og:image:height', content: String(Math.round((outWidth * height) / width)) },
        );
    }
    const alt = String(file?.description || '').trim();
    if (alt) tags.push({ property: 'og:image:alt', content: alt });
    return tags;
}

/** Drop tags with empty content so an outer (site-wide) value shows through. */
export function compactMeta(tags) {
    return tags.filter((tag) => tag && String(tag.content ?? '').trim() !== '');
}

/**
 * Per-file share card. `null` means that field still uses the site-wide template.
 * A string (including '') is this image or video's own text.
 * @param {object|null|undefined} file
 * @returns {{ title: string|null, description: string|null }|null}
 */
export function mediaShareOverride(file) {
    const share = file?.share;
    if (!share || typeof share !== 'object') return null;
    const title = share.title == null ? null : String(share.title);
    const description = share.description == null ? null : String(share.description);
    if (title == null && description == null) return null;
    return { title, description };
}

/**
 * Title + description for a media (.img / .vid) share card.
 *
 * Matches the Appearance preview. A per-file `file.share` title or description
 * replaces the site-wide template for that image or video. Otherwise the title
 * and description come from `media_share_title` / `media_share_description`
 * (`{parent}` / `{site}` / `{title}`).
 *
 * @param {object|null|undefined} file
 * @param {{
 *   content?: object,
 *   theme?: object,
 *   page?: object|null,
 *   pageId?: string|null,
 *   pages?: object,
 *   isHome?: boolean,
 * }} [opts]
 * @returns {{ title: string, description: string }}
 */
export function resolveMediaShareCard(file, opts = {}) {
    const content = opts.content || {};
    const pages = opts.pages || content.pages || {};
    const isHome = Boolean(opts.isHome);
    const page = opts.page || null;
    const mediaTitle = String(file?.description || '').trim();
    const site = shareSiteTitle(content, opts.theme);
    const parent = String(
        page?.name || (isHome ? 'Home' : ''),
    ).trim();
    const vars = { parent, site, title: mediaTitle };
    const override = mediaShareOverride(file);
    const siteTitle = resolveShareTitle(content?.properties?.media_share_title, vars);
    const siteDescription = resolveShareTitle(
        content?.properties?.media_share_description,
        vars,
        '{title}',
        { strict: true },
    );
    const title = override?.title != null && override.title.trim()
        ? resolveShareTitle(override.title, vars, override.title, { strict: true })
        : siteTitle;
    const description = override?.description != null
        ? resolveShareTitle(override.description, vars, '', { strict: true })
        : siteDescription;

    return { title, description };
}

